// MainLayout.jsx
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
	Box,
	CssBaseline,
	Drawer,
	AppBar,
	Toolbar,
	Typography,
	Avatar,
	useMediaQuery,
	IconButton,
	useTheme,
	Badge,
	Menu,
	MenuItem,
	ListItemText,
	ListItemAvatar,
	Divider,
	Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Notifications, Send, Warning, Check, Close, PlayArrow, Error as ErrorIcon } from "@mui/icons-material";
import Sidebar from "./Sidebar";
import useAuth from "../../hooks/useAuth";
import axios from "axios";

// Notification Bell Component
function NotificationBell() {
	const navigate = useNavigate();
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [anchorEl, setAnchorEl] = useState(null);

	const fetchNotifications = async () => {
		try {
			const response = await axios.get("/api/notifications");
			setNotifications(response.data.notifications || []);
			setUnreadCount((response.data.notifications || []).filter((n) => !n.is_read).length);
		} catch (error) {
			console.error("Error fetching notifications:", error);
			setNotifications([]);
			setUnreadCount(0);
		}
	};

	useEffect(() => {
		fetchNotifications();
		// Poll for new notifications every minute
		const interval = setInterval(fetchNotifications, 60000);
		return () => clearInterval(interval);
	}, []);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const markAsRead = async (id) => {
		try {
			await axios.put(`/api/notifications/${id}/read`);
			fetchNotifications();
		} catch (error) {
			console.error("Error marking notification as read:", error);
		}
	};

	const markAllAsRead = async () => {
		try {
			await axios.put("/api/notifications/read-all");
			fetchNotifications();
		} catch (error) {
			console.error("Error marking all notifications as read:", error);
		}
	};

	const handleNotificationClick = async (notification) => {
		try {
			// Parse the data if needed
			let data = notification.data;
			if (typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (e) {
					console.error("Error parsing notification data:", e);
					data = {};
				}
			}

			// Handle different notification types
			if (notification.type === "access_request") {
				// Navigate directly to user detail page
				if (data && data.user_id) {
					navigate(`/admin/users/${data.user_id}`, {
						state: {
							showAccessRequest: true,
							requestData: {
								id: data.request_id,
								reason: data.reason,
								username: data.username,
								timestamp: notification.created_at,
							},
						},
					});
				} else {
					// Fallback if no user_id is available
					navigate("/admin/users");
				}
			} else if (notification.type === "access_request_update") {
				// For users receiving approval/rejection notifications
				// Navigate to bot list to show their accessible bots
				navigate("/bots");
			} else {
				// For other notification types, just mark as read
				console.log("Generic notification clicked:", notification);
			}

			// Mark the notification as read
			await markAsRead(notification.id);

			// Close the menu
			handleClose();
		} catch (error) {
			console.error("Error handling notification click:", error);
		}
	};

	// Helper function to render notification
	const renderNotification = (notification) => {
		// Parse data if needed
		let data = notification.data;
		if (typeof data === "string") {
			try {
				data = JSON.parse(data);
			} catch (e) {
				console.error("Error parsing notification data:", e);
				data = {};
			}
		}

		// Determine icon and color based on notification type
		let icon = <Notifications />;
		let iconColor = "primary.main";

		if (notification.type === "access_request") {
			icon = <Send />;
			iconColor = "#6c5ce7"; // Purple for access requests
		} else if (notification.type === "access_request_update") {
			if (data && data.status === "approved") {
				icon = <Check />;
				iconColor = "#55efc4"; // Green for approvals
			} else {
				icon = <Close />;
				iconColor = "#ff7675"; // Red for rejections
			}
		} else if (notification.type === "system_warning") {
			icon = <Warning />;
			iconColor = "#ffa502"; // Orange for warnings
		} else if (notification.type === "bot_status") {
			if (data && data.status === "running") {
				icon = <PlayArrow />;
				iconColor = "#55efc4"; // Green for running bots
			} else if (data && data.status === "error") {
				icon = <ErrorIcon />;
				iconColor = "#ff7675"; // Red for bot errors
			}
		}

		return (
			<MenuItem
				key={notification.id}
				onClick={() => handleNotificationClick(notification)}
				sx={{
					bgcolor: notification.is_read ? "transparent" : "rgba(0, 0, 0, 0.04)",
					py: 1.5,
				}}
			>
				<ListItemAvatar>
					<Avatar sx={{ bgcolor: notification.is_read ? "action.disabled" : iconColor }}>{icon}</Avatar>
				</ListItemAvatar>
				<ListItemText
					primary={notification.message}
					secondary={new Date(notification.created_at).toLocaleString()}
					primaryTypographyProps={{
						variant: "body2",
						fontWeight: notification.is_read ? "normal" : "bold",
					}}
				/>
			</MenuItem>
		);
	};

	return (
		<>
			<IconButton color="inherit" onClick={handleClick}>
				<Badge badgeContent={unreadCount} color="error">
					<Notifications />
				</Badge>
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				PaperProps={{
					sx: { width: 360, maxHeight: 500, overflow: "auto" },
				}}
			>
				<Box sx={{ p: 2, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6">Notifications</Typography>
					{notifications.length > 0 && (
						<Button size="small" onClick={markAllAsRead}>
							Mark all read
						</Button>
					)}
				</Box>
				<Divider />

				{notifications.length === 0 ? (
					<MenuItem disabled>
						<ListItemText primary="No notifications" />
					</MenuItem>
				) : (
					notifications.map((notification) => renderNotification(notification))
				)}
			</Menu>
		</>
	);
}

function MainLayout() {
	const { user, isAdmin } = useAuth();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [mobileOpen, setMobileOpen] = useState(false);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const drawerWidth = 240;

	return (
		<Box sx={{ display: "flex", minHeight: "100vh", width: "100%" }}>
			<CssBaseline />

			<AppBar
				position="fixed"
				color="default"
				elevation={1}
				sx={{
					zIndex: (theme) => theme.zIndex.drawer + 1,
					backgroundColor: "white",
					color: "text.primary",
					width: "100%",
				}}
			>
				<Toolbar sx={{ justifyContent: "space-between" }}>
					{isMobile && (
						<IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
							<MenuIcon />
						</IconButton>
					)}

					<Typography variant="h6" noWrap component="div" sx={{ fontWeight: "bold" }}>
						Discord Bot Platform
					</Typography>

					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						{/* Notification Bell for ALL users, not just admin */}
						{user && <NotificationBell />}

						<Typography variant="body2">{user?.username}</Typography>
						<Avatar sx={{ bgcolor: "primary.main" }}>{user?.username?.[0]?.toUpperCase()}</Avatar>
					</Box>
				</Toolbar>
			</AppBar>

			<Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
				{/* Mobile drawer */}
				{isMobile && (
					<Drawer
						variant="temporary"
						open={mobileOpen}
						onClose={handleDrawerToggle}
						ModalProps={{
							keepMounted: true, // Better mobile performance
						}}
						sx={{
							"& .MuiDrawer-paper": {
								boxSizing: "border-box",
								width: drawerWidth,
								border: "none",
								backgroundColor: "#ffffff",
							},
						}}
					>
						<Sidebar />
					</Drawer>
				)}

				{/* Desktop drawer */}
				<Drawer
					variant="permanent"
					sx={{
						display: { xs: "none", md: "block" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
							border: "none",
							backgroundColor: "#ffffff",
						},
					}}
					open
				>
					<Toolbar /> {/* Spacer to push content below AppBar */}
					<Sidebar />
				</Drawer>
			</Box>

			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 3,
					backgroundColor: "#f8f9fa",
					marginTop: "64px",
					width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
					maxWidth: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
					boxSizing: "border-box",
					overflow: "hidden",
				}}
			>
				<Outlet />
			</Box>
		</Box>
	);
}

export default MainLayout;
